import { useState, useEffect, useRef } from 'react';
import withCache from '../../common/with-cache';
export const events = {
    RELOAD: 'RELOAD',
    INVALIDATE_CACHE: 'INVALIDATE_CACHE',
    MANAGE_CONTROLS: 'MANAGE_CONTROLS',
    MANAGE_DATA: 'MANAGE_DATA',
};
const CUSTOM_FILTER_INIT = Symbol('CUSTOM_FILTER_INIT');
const useCustomFilters = (filters, onUpdate = () => { }) => {
    const [, forceUpdate] = useState(1);
    const [customFilters, setCustomFilters] = useState(filters?.reduce((acc, filter) => {
        const { name, initialValue } = filter;
        acc[name] = { value: initialValue };
        return acc;
    }, { [CUSTOM_FILTER_INIT]: true }));
    const throttleRef = useRef(customFilters);
    if (customFilters && customFilters[CUSTOM_FILTER_INIT]) {
        delete customFilters[CUSTOM_FILTER_INIT];
        delete throttleRef.current[CUSTOM_FILTER_INIT];
        filters?.forEach(({ name, throttle = 0 }) => {
            customFilters[name].onChange = (newValue) => {
                const prevCall = throttleRef.current[name].lastCalled || 0;
                const now = +new Date();
                if (now - prevCall >= throttle) {
                    throttleRef.current = { ...throttleRef.current };
                    throttleRef.current[name].value = newValue;
                    throttleRef.current[name].lastCalled = now;
                    forceUpdate((i) => i + 1);
                }
                else {
                    clearTimeout(throttleRef.current[name].timeoutId);
                    throttleRef.current[name].timeoutId = setTimeout(() => {
                        throttleRef.current = { ...throttleRef.current };
                        throttleRef.current[name].value = newValue;
                        throttleRef.current[name].lastCalled = +new Date();
                        forceUpdate((i) => i + 1);
                    }, throttle + prevCall - now);
                }
                setCustomFilters((prev) => ({
                    ...prev,
                    [name]: {
                        ...prev[name],
                        value: newValue,
                    },
                }));
                onUpdate();
            };
        });
    }
    return { customFilters, customFiltersThrottled: throttleRef.current };
};
export default ({ fetcher, pagination, filters, observable, cache }) => {
    const loadIdRef = useRef(1); // to ignore stale response
    const dataWithoutPaginationRef = useRef([]); // used when pagination.isLocal == true
    const cachedFetcherRef = useRef(cache &&
        withCache(fetcher, {
            invalidateTimeout: cache.invalidateTimeout,
        }));
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState();
    const [requestParams, setRequestParams] = useState();
    // sorting
    const [sort, setSort] = useState();
    // pagination
    const [page, setPage] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(pagination?.sizePerPage || 10);
    const [total, setTotal] = useState();
    // other filters
    const { customFilters, customFiltersThrottled } = useCustomFilters(filters, () => setPage(1));
    const controls = {
        sort,
        setSort,
        page,
        setPage,
        sizePerPage,
        setSizePerPage: (val) => {
            setSizePerPage((currentPerPage) => {
                // set page that contain the first row of current page
                setPage((currentPage) => {
                    const topIndex = (currentPage - 1) * currentPerPage + 1;
                    return Math.ceil(topIndex / val);
                });
                return val;
            });
        },
        total,
        customFilters,
    };
    const paginationParams = pagination?.isLocal ? [] : [page, sizePerPage];
    // update request params
    useEffect(() => {
        const customParams = filters?.reduce((acc, filter) => {
            const { name, transform } = filter;
            if (!customFiltersThrottled[name]) {
                return acc;
            }
            const defaultTransform = (value) => ({
                [name]: value,
            });
            return {
                ...acc,
                ...(transform || defaultTransform)(customFilters[name].value),
            };
        }, {});
        const newParams = {
            ...customParams,
            ordering: sort,
        };
        if (!pagination?.isLocal) {
            newParams.page = page;
            newParams.sizePerPage = sizePerPage;
        }
        setRequestParams(newParams);
    }, [sort, customFiltersThrottled, ...paginationParams]);
    const setLocalPagination = () => setData(
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'never[]' is not assignable to pa... Remove this comment to see the full error message
    [...dataWithoutPaginationRef.current].splice((page - 1) * sizePerPage, sizePerPage));
    useEffect(() => {
        if (pagination?.isLocal) {
            setLocalPagination();
        }
    }, [page, sizePerPage]);
    const load = async () => {
        if (!requestParams) {
            return;
        }
        loadIdRef.current += 1;
        const loadId = loadIdRef.current;
        setIsLoading(true);
        try {
            const response = await (cachedFetcherRef.current?.call || fetcher)(requestParams);
            if (loadId !== loadIdRef.current) {
                return;
            }
            if (pagination?.isLocal) {
                setTotal(response.length);
                dataWithoutPaginationRef.current = response;
                setLocalPagination();
            }
            else {
                setTotal((pagination?.getTotal || ((r) => r.count))(response));
                setData(response.data); // todo: transform
            }
        }
        catch (error) {
            console.log('table fetcher error', error);
        }
        setIsLoading(false);
    };
    useEffect(() => {
        if (!observable) {
            return undefined;
        }
        const clearFunctions = [];
        clearFunctions.push(observable.on(events.RELOAD, () => {
            cachedFetcherRef.current?.invalidate(); // not sure about invalidation here
            load();
        }));
        clearFunctions.push(observable.on(events.INVALIDATE_CACHE, cachedFetcherRef.current?.invalidate));
        clearFunctions.push(observable.on(events.MANAGE_CONTROLS, (cb) => cb(controls)));
        clearFunctions.push(observable.on(events.MANAGE_DATA, (cb) => cb({ data, setData })));
        return () => clearFunctions.forEach((clearFn) => clearFn());
    }, [requestParams, data]);
    useEffect(() => {
        load();
    }, [requestParams]);
    return {
        data,
        isLoading,
        controls,
    };
};
